import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { List, X, Phone, CaretDown } from "@phosphor-icons/react";
import { NAV, COMPANY } from "@/lib/data";
import { useSite, phoneHref } from "@/context/SiteContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const content = useSite();

  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  // Logo/Home must always return to the homepage; if already there, scroll to top.
  const goHome = (e) => {
    setOpen(false);
    setServicesOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`relative z-50 w-full transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-sm" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl min-[1560px]:max-w-[1720px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-[76px] lg:h-[92px]">
        <Link to="/" onClick={goHome} data-testid="nav-home-logo" aria-label="CareDeserved home" className="shrink-0">
          <Logo className="h-14 lg:h-[68px]" />
        </Link>

        {/* Desktop nav — shown only when there is genuinely room (>=1560px); collapses to hamburger below */}
        <nav className="hidden min-[1560px]:flex items-center gap-0" aria-label="Primary">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <Link
                  to={item.to}
                  data-testid="nav-services"
                  className={`flex items-center gap-1 rounded-full whitespace-nowrap px-2.5 py-2 text-sm font-medium transition-colors ${
                    isActive(item.to) ? "text-brand-blue" : "text-slate-700 hover:text-brand-blue"
                  }`}
                >
                  {item.label} <CaretDown size={13} weight="bold" className="mt-0.5 transition-transform group-hover:rotate-180" />
                </Link>
                <div className="invisible absolute left-0 top-full pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-dd-${c.to.replace(/\//g, "-")}`}
                        className="block rounded-xl px-3 py-2.5 text-sm text-slate-700 transition-colors hover:bg-brand-bg hover:text-brand-blue"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.to + item.label}
                to={item.to}
                onClick={item.to === "/" ? goHome : undefined}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full whitespace-nowrap px-2.5 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to) ? "text-brand-blue" : "text-slate-700 hover:text-brand-blue"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Phone + Book a Ride — all desktop/laptop widths (>=1024px), never wraps */}
          <a
            href={phoneHref(content.phone)}
            data-testid="nav-phone"
            className="hidden lg:flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-semibold text-brand-blue hover:text-brand-purple transition-colors"
          >
            <Phone size={18} weight="fill" className="shrink-0" /> {content.phone}
          </a>
          <Button
            data-testid="nav-book-ride"
            onClick={() => navigate("/book-a-ride")}
            className="hidden lg:inline-flex h-11 shrink-0 whitespace-nowrap rounded-full bg-brand-purple px-5 font-semibold text-white hover:bg-brand-purple2"
          >
            Book a Ride
          </Button>
          {/* Compact call button — mobile only (<1024px) */}
          <a
            href={phoneHref(content.phone)}
            data-testid="nav-phone-mobile"
            aria-label="Call CareDeserved"
            className="flex lg:hidden h-11 w-11 items-center justify-center rounded-full bg-brand-purple text-white"
          >
            <Phone size={20} weight="fill" />
          </a>
          {/* Hamburger — collapses nav below 1560px instead of overflowing/wrapping */}
          <button
            data-testid="nav-hamburger"
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className="flex min-[1560px]:hidden h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-brand-blue"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="min-[1560px]:hidden border-t border-slate-200 bg-white">
          <nav className="mx-auto max-w-7xl px-4 py-3 space-y-1">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setServicesOpen((s) => !s)}
                    data-testid="mobile-services-toggle"
                    className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-base font-medium text-slate-800"
                  >
                    {item.label} <CaretDown size={16} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen && (
                    <div className="ml-3 border-l border-slate-200 pl-3">
                      {item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="block rounded-lg px-3 py-2.5 text-sm text-slate-600 hover:text-brand-blue">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to + item.label}
                  to={item.to}
                  onClick={item.to === "/" ? goHome : undefined}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="block rounded-xl px-3 py-3 text-base font-medium text-slate-800 hover:bg-brand-bg"
                >
                  {item.label}
                </Link>
              )
            )}
            <Button
              data-testid="mobile-book-ride"
              onClick={() => navigate("/book-a-ride")}
              className="mt-2 h-12 w-full rounded-full bg-brand-purple font-semibold text-white hover:bg-brand-purple2"
            >
              Book a Ride
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
